import { Map } from "common/utils/Map";
import { Route, Params, Fn } from "../types";

import pako from 'pako';

import { roundRouteDistance } from 'entities/map';
import getGUID from "common/utils/getGUID";
import { getWaypointIcon } from "entities/map";
import { intl } from 'i18n/Translations';
import { getInfoWindow } from "../utils";

import { ROUTE_TYPES } from 'entities/map';

export const AVERAGE_OVERVIEW_PATH_ITEMS = 300;

export class HereRoute implements Route {
  private _map: Map;
  private _id: string;
  private _routeLine?: any;
  private _group?: any;
  private _waypointsGroup?: any;
  private _infoWindow?: any;
  private _lineString: H.geo.LineString = new window.H.geo.LineString();
  public routePoints: Params['routePoints'];
  public time: Params["time"];
  public startAddress: Params["startAddress"];
  public endAddress: Params["endAddress"];
  public distance: Params["distance"];
  public bounds: Params["bounds"];
  public legs: Params["legs"];
  public path: Route["path"];
  public polyline: Params['polyline'];
  public stationCodes: Params['stationCodes'];
  public shortPath: Route['shortPath'] = [];
  public tags: Route['tags'];
  public costSummary: Route['costSummary'];
  public duration: Route['duration'] = 0;
  isFastest: Route['isFastest'] = false;
  isCheapest: Route['isCheapest'] = false;
  stationRecommendations: Route['stationRecommendations'];
  private _polylineOptions = {
    strokeColor: "#008DC9",
    lineWidth: 5,
  };

  constructor(map: Map, params: Params) {
    const {
      time,
      startAddress,
      endAddress,
      distance,
      bounds,
      legs,
      routePoints,
      stationCodes,
      path,
      polyline,
      tags,
      costSummary,
      duration,
      stationRecommendations
    } = params;

    this._map = map;
    this._id = getGUID();
    this.time = time;
    this.startAddress = startAddress;
    this.endAddress = endAddress;
    this.distance = distance;
    this.bounds = bounds;
    this.legs = legs;
    this.routePoints = routePoints;
    this.stationCodes = stationCodes;
    this.polyline = polyline;
    this.path = path;
    this.tags = tags;
    this.costSummary = costSummary;
    this.isFastest = (tags || []).includes(ROUTE_TYPES.fastest);
    this.isCheapest = (tags || []).includes(ROUTE_TYPES.cheapest);
    this.duration = duration || 0;
    this.stationRecommendations = stationRecommendations;
    this._initGroup();
    this._initPath();
    this._initLineString();
    this._initRouteLine();
    this._addRouteLineToGroup();
  }

  public get id() {
    return this._id;
  }

  _initPath() {
    if (!this.path && this.polyline) {
      const lineString = window.H.geo.LineString.fromFlexiblePolyline(this.polyline);
      // @ts-ignore
      this.path = lineString.toGeoJSON().coordinates.map(([lng, lat]: [number, number]) => ({
        lat,
        lng
      }));
    }
  }

  _initLineString() {
    if (this.polyline) {
      this._lineString = window.H.geo.LineString.fromFlexiblePolyline(this.polyline);
      return;
    }

    if (this.path) {
      this.path.forEach(({ lat, lng }) => {
        this._lineString.pushLatLngAlt(lat, lng, 0);
      });
    }
  }

  _getOverviewPathMidpoint() {
    if (this.path) {
      return this.path[Math.round(this.path.length / 2)];
    }

    return { lat: 0, lng: 0 };
  }

  _initRouteLine() {
    this._routeLine = new window.H.map.Polyline(this._lineString, {
      style: {
        ...this._polylineOptions,
      },
      zIndex: 0,
    });
  }

  _initGroup() {
    this._group = new window.H.map.Group();
  }

  _addRouteLineToGroup() {
    if (this._group) {
      this._group.addObject(this._routeLine);
    }
  }

  _addWaypointMarkers() {
    if (this.routePoints) {
      this._waypointsGroup = new window.H.map.Group();
      const routePoints = [
        this.routePoints.firstPoint,
        ...this.routePoints.waypoints,
        this.routePoints.lastPoint
      ];

      routePoints.forEach(({ coordinates }, index) => {
        let name = '';
        
        if (index === 0) {
          name = 'startPoint';
        } else if (index === routePoints.length - 1) {
          name = 'endPoint';
        } else {
          name = `waypoint${index}`;
        }

        this._waypointsGroup.addObject(
          // @ts-ignore
          new window.H.map.Marker({
            lat: Number(coordinates?.lat),
            lng: Number(coordinates?.lng),
          }, {
            icon: new window.H.map.Icon(getWaypointIcon(name), {
              size: {
                w: 32,
                h: 32,
              },
            }),
          })
        );
      });

      this._map.getNativeMap().addObject(this._waypointsGroup);
    }
  }

  _deleteWaypointMarkers() {
    this._map.getNativeMap().removeObject(this._waypointsGroup);
  }

  addInfoWindow() {
    this._infoWindow = new window.H.ui.InfoBubble(
      this._getOverviewPathMidpoint(),
      {
        content: getInfoWindow({
          distance: `${roundRouteDistance(this.distance)} ${intl.formatMessage({ id: 'km' })}` ,
          time: this.time,
        }),
      }
    );
    this._map.mapUi.addBubble(this._infoWindow);
  }

  deleteInfoWindow() {
    this._map.mapUi.removeBubble(this._infoWindow);
  }

  selectRoute(isSelected: boolean) {
    this._routeLine.setStyle({
      ...this._polylineOptions,
      strokeColor: isSelected ? "#4FBC46" : this._polylineOptions.strokeColor,
    });

    this._group.setZIndex(isSelected ? 5 : 0);
  }

  show() {
    this._map.getNativeMap().addObject(this._group);
    this._addWaypointMarkers();
  }

  delete() {
    this._map.getNativeMap().removeObject(this._group);
    this._deleteWaypointMarkers();
    this.deleteInfoWindow();
  }

  addListenerClick(fn: Fn) {}

  getReportRouteSettings() {
    const coordinates = (this.path || []).reduce((acc, item) => {
      acc += `${item.lat},${item.lng};`
      return acc;
    }, '');
    const bytes = pako.deflate(coordinates);
    const decodedPath = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');

    return ({
      time: this.time,
      distance: this.distance,
      bounds: this.bounds,
      legs: this.legs,
      routePoints: this.routePoints,
      decodedPath
    });
  }
}
